import Icon from '@/components/utilities/Icon'
import clsx from 'clsx'
import React, { useEffect } from 'react'
import { useMediaQuery } from 'react-responsive'

interface FullModalContainerProps {
  id: string
  title: string
  onClose?: () => void
  children: React.ReactNode
  animation?: string
}

/**
 * Wrapper component providing background, padding, shadow and
 * covers the entire screen. Provide your own children.
 *
 * @example
 * const children = (
 *  <div>This is the content</div>
 * )
 *
 * <FullModalContainer
 *  isActive={isModalActive}
 *  title="Fabric Types"
 *  onClose={handleClose}
 * >
 *     <h1>Contents here</h1>
 * </FullModalContainer>
 *
 * @component
 */
const FullModalContainer = ({
  id,
  title,
  onClose,
  children,
  animation,
}: FullModalContainerProps) => {
  const [toggleSliding, setToggleSliding] = React.useState<boolean>(false)
  const isMobile = useMediaQuery({ maxWidth: 768 })

  useEffect(() => {
    document.body.classList.add('overflow-hidden')
    if (!!animation && animation === 'slide') setToggleSliding(true)
    return () => {
      document.body.classList.remove('overflow-hidden')
    }
  }, [])

  const iconSrc = '/icons/ic-close.svg'
  return (
    <div
      id={`modal-${id}`}
      className={clsx(
        'fixed bg-white left-auto right-auto top-0 opacity-1 max-w-[500px] w-full shadow overflow-hidden h-full z-[100] transition-all',
        !isMobile && 'max-w-full left-0'
      )}
    >
      {!!onClose && (
        <div className='flex h-[60px] gap-[18px] text-base font-semibold items-center p-0 px-4 capitalize'>
          <div
            className='cursor-pointer mt-1.5'
            onClick={onClose}
            data-test={`close-modal-${id}-button`}
          >
            <Icon height={25} width={25} iconSrc={iconSrc} alt='close' lazyLoad={false} />
          </div>
          <div>{title}</div>
        </div>
      )}
      <div data-test={`modal-${id}-children`} className='h-full'>
        {children}
      </div>
    </div>
  )
}

export default FullModalContainer
